import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import QuietHourCard from './QuietHourCard'
import QuietHourModal from './QuietHourModal'

export default function QuietHoursSection({ quietHours = [], timezone, onChange }) {
  const { t } = useTranslation('settings')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingQuietHour, setEditingQuietHour] = useState(null)

  const activeCount = quietHours.filter(qh => qh.enabled).length

  const handleAdd = () => {
    setEditingQuietHour(null)
    setIsModalOpen(true)
  }

  const handleEdit = (qh) => {
    setEditingQuietHour(qh)
    setIsModalOpen(true)
  }

  const handleDelete = (id) => {
    if (window.confirm(t('quietHours.confirmDelete'))) {
      onChange(quietHours.filter(qh => qh.id !== id))
    }
  }

  const handleToggle = (id) => {
    onChange(quietHours.map(qh =>
      qh.id === id ? { ...qh, enabled: !qh.enabled } : qh
    ))
  }

  const handleSave = (quietHour) => {
    if (editingQuietHour) {
      onChange(quietHours.map(qh =>
        qh.id === quietHour.id ? quietHour : qh
      ))
    } else {
      if (quietHours.length >= 10) {
        // Don't use alert, just close modal - button should be disabled anyway
        return
      }
      onChange([...quietHours, quietHour])
    }
    setIsModalOpen(false)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('quietHours.title')}</h2>
          <p className="mt-1 text-sm text-gray-600">
            {t('quietHours.description')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {quietHours.length > 0 && (
            <span className={`px-3 py-1 text-sm font-medium rounded-full border ${
              activeCount === 0
                ? 'bg-gray-100 text-gray-600 border-gray-200'
                : activeCount <= 5
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-yellow-100 text-yellow-700 border-yellow-200'
            }`}>
              {t('quietHours.activeCount', { count: activeCount })}
            </span>
          )}
          <div className="flex flex-col items-end">
            <button
              onClick={handleAdd}
              disabled={quietHours.length >= 10}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('quietHours.add')}
            </button>
            {quietHours.length >= 10 && (
              <p className="text-xs text-amber-600 mt-1">{t('quietHours.maxReached')}</p>
            )}
          </div>
        </div>
      </div>

      {/* List or Empty State */}
      {quietHours.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900">{t('quietHours.empty.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('quietHours.empty.description')}
          </p>
          <button
            onClick={handleAdd}
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg"
          >
            {t('quietHours.empty.button')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {quietHours.map(qh => (
            <QuietHourCard
              key={qh.id}
              quietHour={qh}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Info box */}
      {quietHours.length > 0 && (
        <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-800">
            {t('quietHours.info', { timezone: timezone || 'UTC' })}
          </p>
        </div>
      )}

      {/* Modal */}
      <QuietHourModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editingQuietHour={editingQuietHour}
        existingQuietHours={quietHours}
      />
    </div>
  )
}
